import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';

export function useEventCopyMarkdown({
  event,
  customer,
  eventManager,
  dateDisplay,
  startTime,
  endTime,
  locationDisplay,
  pax,
  formattedTotal,
  pricingLocked,
  activeBreakdown,
  childAllocations,
  getResourceName,
  formatAllocationTime,
  attributesByCategory,
}) {
  const { t } = useI18n();
  const copying = ref(false);

  function val(refOrComputed) {
    return refOrComputed?.value ?? null;
  }

  function buildMarkdown() {
    const ev = val(event);
    if (!ev) return '';

    const sections = [];

    // Title + Status
    sections.push(
      `# ${ev.name}\n\n**Status:** ${ev.custom_status || ev.system_stage}`
    );

    // Key information
    const keyInfo = [
      `- **Date:** ${val(dateDisplay) || 'TBD'}`,
      `- **Time:** ${val(startTime) || 'TBA'} – ${val(endTime) || 'TBA'}`,
      `- **Location:** ${val(locationDisplay) || 'No location assigned'}`,
    ];
    const paxVal = val(pax);
    if (paxVal) keyInfo.push(`- **Guests:** ${paxVal}`);
    sections.push(`## Key Information\n\n${keyInfo.join('\n')}`);

    // Customer
    const c = val(customer);
    if (c) {
      const customerLines = [`- **Name:** ${c.name || 'Unknown'}`];
      if (c.email) customerLines.push(`- **Email:** ${c.email}`);
      if (c.phone_number) customerLines.push(`- **Phone:** ${c.phone_number}`);
      const company =
        c.additional_attributes?.company_name ||
        c.custom_attributes?.organization;
      if (company) customerLines.push(`- **Company:** ${company}`);
      sections.push(`## Customer\n\n${customerLines.join('\n')}`);
    }

    // Event Manager
    const mgr = val(eventManager);
    if (mgr) {
      sections.push(`**Event Manager:** ${mgr.name}`);
    }

    // Description
    if (ev.description) {
      const plainText = ev.description
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/p>/gi, '\n')
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .trim();
      sections.push(`## Description\n\n${plainText}`);
    }

    // Custom Attributes
    const categories = val(attributesByCategory);
    if (categories && Object.keys(categories).length > 0) {
      const attrLines = [];
      Object.entries(categories).forEach(([category, attrs]) => {
        if (category) attrLines.push(`### ${category}\n`);
        attrs.forEach(attr => {
          const v = ev.custom_attributes?.[attr.attribute_key];
          if (v !== undefined && v !== null && v !== '') {
            attrLines.push(`- **${attr.attribute_display_name}:** ${v}`);
          }
        });
      });
      if (attrLines.length > 0) {
        sections.push(`## Custom Attributes\n\n${attrLines.join('\n')}`);
      }
    }

    // Schedule / Allocations
    const allocations = val(childAllocations);
    if (allocations && allocations.length > 0) {
      const scheduleLines = allocations.map(alloc => {
        const resource = getResourceName(alloc);
        const time = formatAllocationTime(alloc);
        const name = alloc.title || alloc.name || resource;
        const suffix = resource && resource !== name ? ` (${resource})` : '';
        return `- ${time} — ${name}${suffix}`;
      });
      sections.push(`## Schedule\n\n${scheduleLines.join('\n')}`);
    }

    // Pricing
    const total = val(formattedTotal);
    if (total) {
      const locked = val(pricingLocked);
      const pricingLines = [
        `- **Total:** ${total}${locked ? ' (locked)' : ''}`,
      ];
      const bd = val(activeBreakdown);
      if (bd?.line_items?.length) {
        pricingLines.push('');
        pricingLines.push('| Item | Qty | Unit Price | Total |');
        pricingLines.push('|------|-----|-----------|-------|');
        bd.line_items.forEach(item => {
          const qty = item.quantity ?? '';
          const unit =
            item.unit_price != null
              ? fmtCurrency(item.unit_price, bd.currency)
              : '';
          const itemTotal =
            item.total != null ? fmtCurrency(item.total, bd.currency) : '';
          pricingLines.push(
            `| ${item.label || item.name || '-'} | ${qty} | ${unit} | ${itemTotal} |`
          );
        });
      }
      sections.push(`## Pricing\n\n${pricingLines.join('\n')}`);
    }

    return sections.join('\n\n');
  }

  function fmtCurrency(amount, currency = 'EUR') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  async function copyAsMarkdown() {
    copying.value = true;
    try {
      const markdown = buildMarkdown();
      await copyTextToClipboard(markdown);
      useAlert(t('PLANNING.SHOW.COPIED_MARKDOWN'));
    } catch {
      useAlert(t('PLANNING.SHOW.COPY_FAILED'));
    } finally {
      copying.value = false;
    }
  }

  return { copying, copyAsMarkdown };
}
